import {createApi} from '@reduxjs/toolkit/query/react';
import _ from 'lodash';
import axios from 'axios';
import store from '../store';
import {selectEffectiveOrg} from './session';
import {getSharedDocumentHeader} from './apiUtils';

const axiosBaseQuery =
  ({baseUrl} = {baseUrl: ''}) =>
  async ({url, method = 'GET', data, params, headers}) => {
    const state = store.getState();
    const {session} = state;
    const effectiveOrg = selectEffectiveOrg(state);
    const _headers = {
      'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
      ...getSharedDocumentHeader(),
      ...headers,
    };
    if (!_.isNil(session.accessToken)) {
      _headers.Authorization = `Bearer ${session.accessToken}`;
    }
    try {
      const result = await axios({
        url: `${baseUrl}${url}`,
        method,
        data,
        params,
        headers: _headers,
      });

      return {data: result.data};
    } catch (error) {
      return {data: null, error};
    }
  };

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_API_URL,
  }),
  tagTypes: ['ChildOrgs'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => ({
        url: '/app/whoAmI',
      }),
    }),
    getAccounts: builder.query({
      query: ({paginationId, limit}) => ({
        url: `/app/accounts?pagination_id=${paginationId}&limit=${limit}`,
      }),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.accounts)) {
          return [];
        }
        return _.filter(
          responseData.accounts,
          (_a) =>
            _a.account_number &&
            _a.account_name &&
            _a.account_type &&
            _a.pli_name
        );
      },
    }),
    getAccountMappings: builder.query({
      query: () => ({
        url: '/app/accountMappings',
      }),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.accountMappings)) {
          return [];
        }
        return responseData.accountMappings;
      },
    }),
    getStockMarketData: builder.query({
      query: () => ({
        url: '/app/settings/company-details/stock-market-data',
      }),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.stockMarketData)) {
          return [];
        }
        return responseData.stockMarketData;
      },
    }),
    getDepartments: builder.query({
      query: ({paginationId, limit}) => ({
        url: `/app/departments?pagination_id=${paginationId}&limit=${limit}`,
      }),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.departments)) {
          return [];
        }
        return _.filter(
          responseData.departments,
          (_d) => _d.department_name && _d.pli_name
        );
      },
    }),
    getFSLI: builder.query({
      query: () => ({url: '/app/fslis'}),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.fslis)) {
          return [];
        }
        return responseData.fslis;
      },
    }),
    getOpexMappings: builder.query({
      query: () => ({url: '/app/opexMappings'}),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.opexMappings)) {
          return [];
        }
        return responseData.opexMappings;
      },
    }),
    getOrg: builder.query({
      query: () => ({url: '/app/org'}),
      transformResponse: (responseData) => {
        return _.get(responseData, 'org', {});
      },
    }),
    getOrgs: builder.query({
      query: () => ({url: '/app/orgs'}),
      transformResponse: (responseData) => {
        return _.get(responseData, 'orgs', []);
      },
    }),
    getOrgUsers: builder.query({
      query: ({orgId}) => ({url: `/app/orgs/${orgId}/users`}),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.users)) {
          return [];
        }
        return responseData.users;
      },
    }),
    getPresentationItems: builder.query({
      query: () => ({url: '/app/presentationItems'}),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.items)) {
          return [];
        }
        return responseData.items;
      },
    }),
    // TODO: Is this deprecated? can we remove it?
    getReportDetails: builder.query({
      query: ({reportId, fsliId, pliId}) => ({
        url: `/app/report/${reportId}/${!_.isNil(pliId) ? pliId : fsliId}/details`,
      }),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.details)) {
          return [];
        }
        return _.filter(
          responseData.details,
          (_record) => Math.abs(_record.total) >= 1
        );
      },
    }),
    getReportDetailsV2: builder.query({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let _response = [];
        if (!_.isNil(args.pliIds)) {
          const _promiseResponse = await Promise.all(
            _.map(args.pliIds, async (_id) => {
              return (
                await baseQuery({
                  url: `/app/report/details/${args.reportType}/${_id}`,
                  params: {
                    period: args.period,
                    comparePeriod: args.comparePeriod,
                  },
                })
              ).data;
            })
          );
          const _parsedResult = _.concat(
            ..._.map(_promiseResponse, (_o) => _o.details)
          );
          const _sortedResults = _.sortBy(_parsedResult, ['label']);
          _response = {
            details: _sortedResults,
          };
        } else {
          _response = (
            await baseQuery({
              url: `/app/report/details/${args.reportType}/${args.pliId}`,
              params: {
                period: args.period,
                comparePeriod: args.comparePeriod,
                currentFileId: args.currentFileId,
                compareFileId: args.compareFileId,
              },
            })
          ).data;
        }
        if (_.isNil(_response) || _.isNil(_response.details)) {
          return [];
        }
        return {
          data: _.filter(
            _response.details,
            (_record) =>
              Math.abs(_record.amount) >= 1 ||
              Math.abs(_record.compareAmount) >= 1
          ),
        };
      },
    }),
    getReportingPeriods: builder.query({
      query: () => ({url: '/app/reportingPeriods'}),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.periods)) {
          return [];
        }
        return _.reverse(_.sortBy(responseData.periods, ['endDate']));
      },
    }),
    getReportReconciliation: builder.query({
      query: ({gaapReportId, nonGaapReportId, period}) => ({
        url: '/app/report/reconciliation',
        params: {
          gaapReportId,
          nonGaapReportId,
          period,
        },
      }),
      transformResponse: (responseData) => {
        if (_.isNil(responseData) || _.isNil(responseData.details)) {
          return [];
        }
        return _.filter(responseData.details, (_o) => _.total > 0);
      },
    }),
    getReportsList: builder.query({
      query: ({period, comparePeriod}) => ({
        url: '/app/reports',
        params: {period, comparePeriod},
      }),
    }),
    publishReports: builder.mutation({
      query: () => ({
        url: '/app/reports/publish',
        method: 'POST',
      }),
      transformResponse: (responseData) => {
        return _.get(responseData, 'publishedReports', []);
      },
    }),
    getWorkspaceSignedUrl: builder.query({
      query: ({filename}) => ({
        url: `/app/workspace/${filename}`,
        method: 'GET',
      }),
    }),
    createWorkspaceSignedUrl: builder.mutation({
      query: (filename) => ({
        url: `/app/workspace/${filename}`,
        method: 'POST',
      }),
    }),

    /*********************************************
     * START WORKBOOK
     *********************************************/
    // This is used to populate the center portion of the workbook that highlights discrepancies
    getIndirectCashflowWorkbook: builder.query({
      query: ({period}) => ({
        url: '/app/reports/iscf-workbook/rules',
        params: {period},
      }),
    }),

    // This is used to populate the table head in the workbook, summarizing the balance sheet across two periods and the difference between those periods
    getBalanceSheetSummary: builder.query({
      query: ({period}) => ({
        url: '/app/reports/iscf-workbook/balance-sheet',
        params: {period},
      }),
    }),

    // This is used to populate the bottom CTA section of the workbook
    getCtaSummary: builder.query({
      query: ({period}) => ({
        url: '/app/reports/iscf-workbook/cta',
        params: {period},
      }),
    }),

    getRuleMetadata: builder.query({
      query: ({pliId, indirectPliId, period}) => ({
        url: `/app/reports/iscf-workbook/rules/${pliId}-${indirectPliId}`,
        params: {period},
      }),
    }),

    // This is used to populate the transactions page for a given slug
    // Here is how these transactions properties map to the table columns:
    // - document_number -> Journal Entry
    // - signed_account -> Classified JE Account Line
    // - signed_accounts -> JE Account lines
    // - amount -> Amount
    getRuleTransactions: builder.query({
      query: ({period, pliId, indirectPliId}) => ({
        url: `/app/reports/iscf-workbook/rules/${pliId}-${indirectPliId}/transactions`,
        params: {period},
      }),
    }),

    getRuleUnpaidFixedAssets: builder.query({
      query: ({period, pliId, indirectPliId}) => ({
        url: `/app/reports/iscf-workbook/rules/${pliId}-${indirectPliId}/unpaid-fixed-assets`,
        params: {period},
      }),
    }),

    // This is used to populate the dropdown list in the reclassify modal
    // and can be used to find the name needed for the "current SCF"
    getFSLIOptions: builder.query({
      query: ({pliType}) => ({
        url: `/app/presentationItems`,
        params: {pliType},
      }),
    }),

    reclassifyIndirectFSLI: builder.mutation({
      query: (data) => ({
        url: `/app/reports/iscf-workbook/reclassify`,
        method: 'PATCH',
        data,
      }),
    }),
    /*********************************************
     * END WORKBOOK
     *********************************************/

    updateOrg: builder.mutation({
      query: (orgData) => ({
        url: `/app/org`,
        method: 'PATCH',
        data: orgData,
      }),
    }),
    createChildOrg: builder.mutation({
      query: (orgData) => ({
        url: `/admin/orgs`,
        method: 'POST',
        data: orgData,
      }),
      invalidatesTags: ['ChildOrgs'],
    }),
    updateChildOrg: builder.mutation({
      query: ({orgId, ...orgData}) => ({
        url: `/admin/orgs/${orgId}`,
        method: 'PATCH',
        data: orgData,
      }),
      invalidatesTags: ['ChildOrgs'],
    }),
    addUsersToOrg: builder.mutation({
      query: ({orgId, users}) => ({
        url: `/admin/orgs/${orgId}/users`,
        method: 'POST',
        data: {
          users,
        },
      }),
    }),
    getChildOrgs: builder.query({
      query: ({
        parentOrganizationId,
        page = 1,
        pageSize = 25,
        search = '',
        sortBy = 'name',
        sortOrder = 'asc',
      }) => ({
        url: `/admin/orgs`,
        method: 'GET',
        params: {
          parentOrganizationId,
          page,
          pageSize,
          ...(search && {search}),
          sortBy,
          sortOrder,
        },
      }),
      providesTags: ['ChildOrgs'],
    }),
    removeUserFromParentOrg: builder.mutation({
      query: ({orgId, userId}) => ({
        url: `/admin/orgs/${orgId}/users/${userId}`,
        method: 'DELETE',
      }),
    }),
    getUserMemberships: builder.query({
      // eslint-disable-next-line camelcase
      query: ({parentOrgId, user_ids}) => ({
        url: `/admin/orgs/${parentOrgId}/users/memberships`,
        method: 'GET',
        params: {
          // eslint-disable-next-line camelcase
          user_ids: Array.isArray(user_ids) ? user_ids.join(',') : user_ids,
        },
      }),
    }),
    joinOrganization: builder.mutation({
      query: ({orgId}) => ({
        url: `/admin/orgs/${orgId}/membership`,
        method: 'POST',
      }),
      invalidatesTags: ['ChildOrgs'],
    }),
    leaveOrganization: builder.mutation({
      query: ({orgId}) => ({
        url: `/admin/orgs/${orgId}/membership`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChildOrgs'],
    }),
  }),
});

export const {
  endpoints,
  useGetCurrentUserQuery,
  useGetAccountMappingsQuery,
  useGetAccountsQuery,
  useGetStockMarketDataQuery,
  useGetDepartmentsQuery,
  useGetFSLIQuery,
  useGetOpexMappingsQuery,
  useGetOrgQuery,
  useGetOrgsQuery,
  useGetOrgUsersQuery,
  useGetPresentationItemsQuery,

  // Reports
  useGetReportDetailsQuery,
  useGetReportDetailsV2Query,
  useGetReportDetailsWithChildrenV2Query,
  useGetReportingPeriodsQuery,
  useGetReportReconciliationQuery,
  useGetReportsListQuery,
  usePublishReportsMutation,
  // Start Workbook
  useCreateWorkspaceSignedUrlMutation,
  useGetWorkspaceSignedUrlQuery,
  // useGetIndirectCashFlowSummaryQuery -- queried through useGetReportsListQuery
  useGetIndirectCashflowWorkbookQuery,
  useGetBalanceSheetSummaryQuery,
  useGetCtaSummaryQuery,
  useGetRuleMetadataQuery,
  useGetRuleTransactionsQuery,
  useGetRuleUnpaidFixedAssetsQuery,
  useGetFSLIOptionsQuery,
  useReclassifyIndirectFSLIMutation,
  // End Workbook
  useUpdateOrgMutation,
  useCreateChildOrgMutation,
  useUpdateChildOrgMutation,
  useAddUsersToOrgMutation,
  useGetChildOrgsQuery,
  useRemoveUserFromParentOrgMutation,
  useGetUserMembershipsQuery,
  useJoinOrganizationMutation,
  useLeaveOrganizationMutation,
} = apiSlice;
